/**
 * Onboarding Pipeline Page — kanban-style candidate pipeline grouped by stage.
 *
 * Fetches all candidates, groups by status, and renders pipeline columns.
 * Supports advancing candidates to the next stage via BLL PUT.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  EmptyState,
} from '@yesbos/ui';
import { Plus, Users, ChevronRight } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

// ── Types ────────────────────────────────────────────────────────────────────

type CandidateStatus =
  | 'applied'
  | 'screening'
  | 'offered'
  | 'accepted'
  | 'joining'
  | 'joined'
  | 'withdrawn';

interface Candidate {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  position_applied: string;
  status: CandidateStatus;
  expected_join_date?: string;
  offered_ctc?: number;
  source?: string;
  department_name?: string;
  designation_name?: string;
}

interface BLLResponse {
  success: boolean;
  data?: unknown;
  error?: { message: string };
  meta?: { total: number };
}

// ── Constants ────────────────────────────────────────────────────────────────

const PIPELINE_STAGES: CandidateStatus[] = [
  'applied',
  'screening',
  'offered',
  'accepted',
  'joining',
  'joined',
];

const NEXT_STAGE: Partial<Record<CandidateStatus, CandidateStatus>> = {
  applied: 'screening',
  screening: 'offered',
  offered: 'accepted',
  accepted: 'joining',
  joining: 'joined',
};

const STATUS_LABEL: Record<CandidateStatus, string> = {
  applied: 'Applied',
  screening: 'Screening',
  offered: 'Offered',
  accepted: 'Accepted',
  joining: 'Joining',
  joined: 'Joined',
  withdrawn: 'Withdrawn',
};

const STATUS_BADGE_CLASS: Record<CandidateStatus, string> = {
  applied: 'bg-blue-100 text-blue-700 border-blue-200',
  screening: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  offered: 'bg-purple-100 text-purple-700 border-purple-200',
  accepted: 'bg-green-100 text-green-700 border-green-200',
  joining: 'bg-teal-100 text-teal-700 border-teal-200',
  joined: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  withdrawn: 'bg-red-100 text-red-700 border-red-200',
};

const COLUMN_HEADER_CLASS: Record<CandidateStatus, string> = {
  applied: 'border-l-4 border-l-blue-400',
  screening: 'border-l-4 border-l-yellow-400',
  offered: 'border-l-4 border-l-purple-400',
  accepted: 'border-l-4 border-l-green-400',
  joining: 'border-l-4 border-l-teal-400',
  joined: 'border-l-4 border-l-emerald-400',
  withdrawn: 'border-l-4 border-l-red-400',
};

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(
  path: string,
  method = 'GET',
  body?: unknown,
): Promise<BLLResponse> {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return res.json() as Promise<BLLResponse>;
}

// ── Candidate Card ────────────────────────────────────────────────────────────

interface CandidateCardProps {
  candidate: Candidate;
  advancingId: string | null;
  onAdvance: (id: string, nextStatus: CandidateStatus) => void;
  onView: (id: string) => void;
}

function CandidateCard({
  candidate,
  advancingId,
  onAdvance,
  onView,
}: CandidateCardProps) {
  const next = NEXT_STAGE[candidate.status];

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-3 pb-3">
        <div className="flex items-start justify-between gap-1 mb-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{candidate.full_name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {candidate.position_applied}
            </p>
          </div>
        </div>

        {candidate.department_name && (
          <Badge variant="outline" className="text-xs mb-2">
            {candidate.department_name}
          </Badge>
        )}

        <div className="space-y-1 text-xs text-muted-foreground">
          {candidate.offered_ctc != null && (
            <p>
              CTC:{' '}
              <span className="text-foreground font-medium">
                ₹{candidate.offered_ctc.toLocaleString('en-IN')}
              </span>
            </p>
          )}
          {candidate.expected_join_date && (
            <p>
              Join:{' '}
              {new Date(candidate.expected_join_date).toLocaleDateString(
                'en-IN',
                { day: 'numeric', month: 'short', year: 'numeric' },
              )}
            </p>
          )}
        </div>

        <div className="flex gap-2 mt-3">
          <button
            onClick={() => onView(candidate.id)}
            className="text-xs text-primary hover:underline"
          >
            View
          </button>
          {next && (
            <button
              onClick={() => onAdvance(candidate.id, next)}
              disabled={advancingId === candidate.id}
              className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 ml-auto"
            >
              {advancingId === candidate.id
                ? 'Moving…'
                : `Move to ${STATUS_LABEL[next]}`}
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Pipeline Column ───────────────────────────────────────────────────────────

interface PipelineColumnProps {
  status: CandidateStatus;
  candidates: Candidate[];
  advancingId: string | null;
  onAdvance: (id: string, nextStatus: CandidateStatus) => void;
  onView: (id: string) => void;
}

function PipelineColumn({
  status,
  candidates,
  advancingId,
  onAdvance,
  onView,
}: PipelineColumnProps) {
  return (
    <div className="flex-shrink-0 w-64">
      <Card className={`mb-3 ${COLUMN_HEADER_CLASS[status]}`}>
        <CardHeader className="py-2.5 px-3">
          <CardTitle className="text-sm font-semibold flex items-center justify-between">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATUS_BADGE_CLASS[status]}`}
            >
              {STATUS_LABEL[status]}
            </span>
            <span className="text-muted-foreground font-normal text-xs">
              {candidates.length}
            </span>
          </CardTitle>
        </CardHeader>
      </Card>

      <div className="space-y-2">
        {candidates.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">
            No candidates
          </p>
        ) : (
          candidates.map((c) => (
            <CandidateCard
              key={c.id}
              candidate={c}
              advancingId={advancingId}
              onAdvance={onAdvance}
              onView={onView}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function OnboardingListPage() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = React.useState<Candidate[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [advancingId, setAdvancingId] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // ── Fetch candidates ──────────────────────────────────────────────────────

  const fetchCandidates = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await callBLL('/v1/hros/onboarding/candidates');
      if (res.success && Array.isArray(res.data)) {
        setCandidates(res.data as Candidate[]);
      } else {
        showToast(res.error?.message ?? 'Failed to load candidates.', 'error');
      }
    } catch {
      showToast('Network error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // ── Toast auto-dismiss ────────────────────────────────────────────────────

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ message, type });
  }

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Advance candidate stage ───────────────────────────────────────────────

  async function handleAdvance(id: string, nextStatus: CandidateStatus) {
    setAdvancingId(id);
    try {
      const res = await callBLL(
        `/v1/hros/onboarding/candidates/${id}`,
        'PUT',
        { status: nextStatus },
      );
      if (res.success) {
        showToast(`Candidate moved to ${STATUS_LABEL[nextStatus]}.`, 'success');
        await fetchCandidates();
      } else {
        showToast(res.error?.message ?? 'Failed to advance candidate.', 'error');
      }
    } catch {
      showToast('Network error. Please try again.', 'error');
    } finally {
      setAdvancingId(null);
    }
  }

  function handleView(id: string) {
    navigate(`/onboarding/${id}`);
  }

  // ── Group by status ───────────────────────────────────────────────────────

  const grouped = React.useMemo(() => {
    const map: Record<CandidateStatus, Candidate[]> = {
      applied: [],
      screening: [],
      offered: [],
      accepted: [],
      joining: [],
      joined: [],
      withdrawn: [],
    };
    for (const c of candidates) {
      const key = (c.status ?? 'applied') as CandidateStatus;
      if (map[key]) map[key].push(c);
    }
    return map;
  }, [candidates]);

  const total = candidates.length;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Onboarding Pipeline</h1>
          {!loading && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {total} candidate{total !== 1 ? 's' : ''} in pipeline
            </p>
          )}
        </div>
        <Button onClick={() => navigate('/onboarding/create')}>
          <Plus className="h-4 w-4 mr-2" />
          Add Candidate
        </Button>
      </div>

      {/* Pipeline Board */}
      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {PIPELINE_STAGES.map((s) => (
            <div
              key={s}
              className="flex-shrink-0 w-64 h-48 rounded-lg border bg-muted animate-pulse"
            />
          ))}
        </div>
      ) : total === 0 ? (
        <EmptyState
          icon={<Users className="h-8 w-8" />}
          title="No Candidates Yet"
          description="Add your first candidate to start the onboarding pipeline."
          action={{
            label: 'Add Candidate',
            onClick: () => navigate('/onboarding/create'),
          }}
        />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {PIPELINE_STAGES.map((status) => (
            <PipelineColumn
              key={status}
              status={status}
              candidates={grouped[status]}
              advancingId={advancingId}
              onAdvance={handleAdvance}
              onView={handleView}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default OnboardingListPage;
